/**
 * i18n public entry point.
 *
 * Runtime choice: a small, typed, zero-dependency runtime instead of
 * react-i18next + i18next. Rationale —
 *   1. TypeScript strict, typed keys are a first-class requirement here: the
 *      `TranslationKey` union is derived from the French source so every `t(...)`
 *      call is checked and every locale is held to structural parity at compile
 *      time. That guarantee is native here; with react-i18next it needs extra
 *      `resources` type augmentation and is still weaker for deep dotted keys.
 *   2. Zero new dependencies — this SPA deliberately hand-rolls its shell
 *      (service worker, command palette, icons, confirm dialogs) and already has
 *      zustand, a perfect fit for a reactive locale store. i18next's backends /
 *      language-detector / ICU plural machinery are unused weight for a
 *      French-first CSR app whose foundation needs catalogs + fallback + RTL.
 *   3. The whole runtime is ~4 small modules and mirrors patterns already in the
 *      repo (see stores/ui-store.ts), so it is easy to read and extend.
 *
 * Public API:
 *   • initI18n()            — load the active locale's catalog (lazy chunk unless
 *                             English) + apply it to <html>, before render
 *   • <I18nProvider/>       — root provider keeping <html lang/dir> in sync
 *   • useT() / useTranslation() — the hook: { t, locale, setLocale }
 *   • tr()                  — non-hook translator (module-level copy, helpers)
 *   • i18n.changeLanguage() — imperative language switch (react-i18next-style)
 *   (LanguageSwitcher / switchLocale live in ./LanguageSwitcher + ./profileLocale
 *    and are imported directly — they depend on the API client.)
 *   • config exports        — SUPPORTED_LOCALES, LOCALE_META, RTL_LANGUAGES, …
 */

import { ensureActiveLocaleLoaded } from './store';

export { I18nProvider } from './I18nProvider';
export { useT, useTranslation, translate, tr, intlLocale } from './useT';
export type { TranslationKey, TVars, UseTranslation } from './useT';
export {
  useLocaleStore,
  i18n,
  applyLocaleToDocument,
  persistLocale,
  readLocaleSource,
  ensureActiveLocaleLoaded,
  changeLocale,
} from './store';
export { catalogs, loadLocale, isLocaleLoaded } from './locales';
export type { TranslationKeys } from './locales';
export {
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  FALLBACK_LOCALE,
  RTL_LANGUAGES,
  LOCALE_META,
  LOCALE_STORAGE_KEY,
  LOCALE_SOURCE_KEY,
  detectLocale,
  browserLanguages,
  type LocaleSource,
  isLocale,
  dirFor,
  type Locale,
} from './config';
import type { Locale } from './config';

/**
 * Initialise i18n. Idempotent — fetches the active locale's catalog when it is a
 * lazily-loaded one (English ships in the main bundle) and applies
 * `<html lang/dir>`, so the first paint is in the right language + direction.
 * Await it in the app bootstrap BEFORE importing the application modules.
 */
export function initI18n(): Promise<Locale> {
  return ensureActiveLocaleLoaded();
}
